import { useState, useEffect } from 'react';
import type { FC, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminFetch } from '../utils/api';
import { formatCents, formatDate } from '../utils/format';
import Modal from '../components/Modal';

interface UserAccount {
  cognitoUserId: string;
  email: string;
  balanceCents: number;
  lifetimeDepositCents: number;
  lifetimeSpendCents: number;
  transactionCount: number;
  articleCount?: number;
  displayCurrency?: string;
  stripeCustomerId?: string;
  affiliateCode?: string;
  createdAt: string;
  updatedAt?: string;
}

interface Affiliate {
  code: string;
  name: string;
}

type Mode = 'email' | 'affiliate';

const Users: FC = () => {
  const { token } = useAuth();

  const [mode, setMode] = useState<Mode>('email');
  const [emailQuery, setEmailQuery] = useState('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);

  const [users, setUsers] = useState<UserAccount[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  const [detailUser, setDetailUser] = useState<UserAccount | null>(null);

  useEffect(() => {
    if (!token) return;
    adminFetch<{ affiliates: Affiliate[] }>('/admin/affiliates', token)
      .then(data => setAffiliates(data.affiliates))
      .catch(err => console.error('Failed to load affiliates:', err));
  }, [token]);

  const switchMode = (m: Mode) => {
    setMode(m);
    setUsers([]);
    setHasSearched(false);
    setError('');
  };

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setError('');
    setSearching(true);
    try {
      if (mode === 'email') {
        const q = emailQuery.trim();
        if (!q) { setSearching(false); return; }
        const data = await adminFetch<{ users: UserAccount[] }>(
          `/admin/users/search?q=${encodeURIComponent(q)}`,
          token,
        );
        setUsers(data.users);
      } else {
        if (!affiliateCode) { setSearching(false); return; }
        const data = await adminFetch<{ referredUsers: UserAccount[] }>(
          `/admin/affiliates/${encodeURIComponent(affiliateCode)}`,
          token,
        );
        setUsers(data.referredUsers || []);
      }
      setHasSearched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="p-3">
      <ul className="nav nav-pills mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${mode === 'email' ? 'active' : ''}`}
            onClick={() => switchMode('email')}
          >By email</button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${mode === 'affiliate' ? 'active' : ''}`}
            onClick={() => switchMode('affiliate')}
          >By affiliate</button>
        </li>
      </ul>

      <form onSubmit={handleSearch} className="d-flex align-items-center gap-2 mb-3">
        {mode === 'email' ? (
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Email prefix..."
            value={emailQuery}
            onChange={e => setEmailQuery(e.target.value)}
            autoFocus
          />
        ) : (
          <select
            className="form-select form-select-sm"
            value={affiliateCode}
            onChange={e => setAffiliateCode(e.target.value)}
          >
            <option value="">Select affiliate...</option>
            {affiliates.map(a => (
              <option key={a.code} value={a.code}>{a.code} — {a.name}</option>
            ))}
          </select>
        )}
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={searching || (mode === 'email' ? !emailQuery.trim() : !affiliateCode)}
        >
          {searching ? '...' : 'Search'}
        </button>
      </form>

      {error && <div className="alert alert-warning py-2 small">{error}</div>}

      {hasSearched && (
        <table className="table table-striped table-hover table-sm align-middle mb-0">
          <thead className="table-dark">
            <tr>
              <th>Email</th>
              <th>Affiliate</th>
              <th className="text-end">Balance</th>
              <th className="text-end">Deposits</th>
              <th className="text-end">Spend</th>
              <th className="text-end">Txns</th>
              <th>Signed up</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr
                key={user.cognitoUserId}
                onClick={() => setDetailUser(user)}
                style={{ cursor: 'pointer' }}
              >
                <td className="small">{user.email}</td>
                <td className="small">{user.affiliateCode || '-'}</td>
                <td className="text-end">{formatCents(user.balanceCents)}</td>
                <td className="text-end">{formatCents(user.lifetimeDepositCents)}</td>
                <td className="text-end">{formatCents(user.lifetimeSpendCents)}</td>
                <td className="text-end">{user.transactionCount}</td>
                <td className="small">{formatDate(user.createdAt)}</td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="text-center text-muted">No users found</td></tr>
            )}
          </tbody>
        </table>
      )}

      {detailUser && (
        <UserDetailsDialog user={detailUser} onClose={() => setDetailUser(null)} />
      )}
    </div>
  );
};

const UserDetailsDialog: FC<{ user: UserAccount; onClose: () => void }> = ({ user, onClose }) => {
  const { token } = useAuth();
  const [detail, setDetail] = useState<UserAccount>(user);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    adminFetch<{ user: UserAccount }>(`/admin/users/${user.cognitoUserId}`, token)
      .then(data => setDetail(data.user))
      .catch(err => console.error('Failed to load user detail:', err))
      .finally(() => setLoading(false));
  }, [token, user.cognitoUserId]);

  return (
    <Modal title="User Details" onClose={onClose} size="lg">
      <div className="modal-body">
        {loading && <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>}
        {!loading && (
          <table className="table table-sm mb-0">
            <tbody>
              <tr><td className="text-muted">Email</td><td>{detail.email}</td></tr>
              <tr><td className="text-muted">Cognito ID</td><td><code className="small">{detail.cognitoUserId}</code></td></tr>
              <tr><td className="text-muted">Stripe ID</td><td><code className="small">{detail.stripeCustomerId || '-'}</code></td></tr>
              <tr><td className="text-muted">Affiliate</td><td>{detail.affiliateCode || '-'}</td></tr>
              <tr><td className="text-muted">Balance</td><td className="fw-bold">{formatCents(detail.balanceCents)}</td></tr>
              <tr><td className="text-muted">Lifetime Deposits</td><td>{formatCents(detail.lifetimeDepositCents)}</td></tr>
              <tr><td className="text-muted">Lifetime Spend</td><td>{formatCents(detail.lifetimeSpendCents)}</td></tr>
              <tr><td className="text-muted">Transactions</td><td>{detail.transactionCount}</td></tr>
              <tr><td className="text-muted">Articles</td><td>{detail.articleCount ?? 0}</td></tr>
              <tr><td className="text-muted">Currency</td><td>{detail.displayCurrency || 'GBP'}</td></tr>
              <tr><td className="text-muted">Signed Up</td><td>{formatDate(detail.createdAt)}</td></tr>
              {detail.updatedAt && <tr><td className="text-muted">Updated</td><td>{formatDate(detail.updatedAt)}</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <div className="modal-footer py-2">
        <button type="button" className="btn btn-sm btn-secondary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
};

export default Users;
